import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { KeyRound, ScanLine, ScrollText, Save, UserPlus, X, ChevronLeft, ChevronRight, Search, ExternalLink } from 'lucide-react';
import { ApiError } from '@/lib/apiClient';
import { adminApi } from '@/queries/admin.queries';
import { queryKeys } from '@/queries/keys';
import { useAppSelector } from '@/store/hooks';
import {
  ALL_ROLES,
  USER_STATUSES,
  type AdminOverview,
  type AdminSettings,
  type AdminUserRow,
  type PaginatedUsageResponse,
  type AuditRow,
  type GapRow,
  type KbVersionRow,
  type PaginatedResponse,
} from '@/lib/types';
import { Badge, Button, Spinner, cx } from '@/components/ui';

type Tab = 'overview' | 'governance' | 'audit' | 'kb' | 'gaps' | 'users' | 'usage';
const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'governance', label: 'Governance' },
  { key: 'audit', label: 'Audit log' },
  { key: 'kb', label: 'Knowledge base' },
  { key: 'gaps', label: 'Gaps' },
  { key: 'users', label: 'Users' },
];

const SEARCH_PLACEHOLDERS: Partial<Record<Tab, string>> = {
  audit: 'Search audit logs...',
  gaps: 'Search questions or comments...',
  users: 'Search users by email or name...',
  usage: 'Search users by email or name...',
};

export function AdminView() {
  const user = useAppSelector((s) => s.auth.user);
  const isOwner = user?.roles.includes('owner') ?? false;
  const tabs = isOwner ? [...TABS, { key: 'usage' as Tab, label: 'Usage' }] : TABS;
  const [tab, setTab] = useState<Tab>('overview');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searches, setSearches] = useState<Record<Tab, string>>({
    overview: '',
    governance: '',
    audit: '',
    kb: '',
    gaps: '',
    users: '',
    usage: '',
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        (e.key === '/' || ((e.ctrlKey || e.metaKey) && e.key === 'k')) &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        setSearches((prev) => ({ ...prev, [tab]: '' }));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tab]);

  const currentPlaceholder = SEARCH_PLACEHOLDERS[tab];

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="kicker">FoxIT Admin</p>
        <h1 className="mt-1 text-2xl font-bold text-foreground tracking-tight">
          Admin &amp; governance
        </h1>
        <p className="mt-1 text-muted-foreground">
          Owners and admins manage the rules, the knowledge base, who has access, AI spend limits,
          and a full audit trail. Each area is permission-gated.
        </p>
      </header>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full min-w-0">
        <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-xl bg-muted border border-border min-w-0 max-w-full overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cx(
                'h-8 px-3.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                tab === t.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {currentPlaceholder && (
          <div className="relative w-full sm:w-80 md:w-96 flex-1 max-w-md flex items-center transition-all duration-200">
            <span className="absolute left-3 flex items-center pointer-events-none text-muted-foreground">
              <Search className="h-4 w-4" />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              value={searches[tab]}
              onChange={(e) =>
                setSearches((prev) => ({ ...prev, [tab]: e.target.value }))
              }
              placeholder={currentPlaceholder}
              className={cx(
                'w-full h-9 pl-9 pr-14 rounded-lg bg-input border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition',
                searches[tab] ? 'border-primary/60' : 'border-border',
              )}
            />
            {searches[tab] && (
              <button
                onClick={() =>
                  setSearches((prev) => ({ ...prev, [tab]: '' }))
                }
                className="absolute right-3 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="animate-rise" key={tab}>
        {tab === 'overview' && <Overview />}
        {tab === 'governance' && <Governance />}
        {tab === 'audit' && <Audit search={searches.audit} />}
        {tab === 'kb' && <Kb />}
        {tab === 'gaps' && <Gaps search={searches.gaps} />}
        {tab === 'users' && <Users search={searches.users} />}
        {tab === 'usage' && isOwner && <Usage search={searches.usage} />}
      </div>
    </div>
  );
}

function useLoad<T>(
  key: string,
  loader: () => Promise<T>,
  params?: { search?: string; page?: number; limit?: number },
): { data: T | null; error: string | null } {
  const query = useQuery({
    queryKey: queryKeys.admin(key, params?.search, params?.page, params?.limit),
    queryFn: loader,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const error = query.isError
    ? query.error instanceof ApiError && query.error.status === 403
      ? 'You do not have access to this area.'
      : 'Failed to load'
    : null;
  return { data: query.data ?? null, error };
}

function State({ children, tone }: { children: ReactNode; tone?: 'error' | 'muted' }) {
  return (
    <div
      className={cx(
        'card p-8 text-center',
        tone === 'error' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {head.map((h) => (
                <th
                  key={h}
                  className="text-left font-medium text-muted-foreground text-xs uppercase tracking-wider px-4 py-3"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="[&>tr]:border-b [&>tr]:border-border [&>tr:last-child]:border-0 [&>tr]:transition-colors [&>tr:hover]:bg-muted">
            {children}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const td = 'px-4 py-3 text-foreground align-top';

function Overview() {
  const { data, error } = useLoad<AdminOverview>('overview', () => adminApi.overview());
  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;
  const cards: { label: string; value: string | number; accent?: boolean }[] = [
    { label: 'Standardizer runs', value: data.standardizerRuns },
    { label: 'FoxIT answered', value: data.foxitAnswered, accent: true },
    { label: 'FoxIT deflected', value: data.foxitDeflected },
    { label: 'Downloads', value: data.downloads },
    { label: 'Active users', value: data.activeUsers },
    { label: 'KB documents', value: data.kbDocCount, accent: true },
    { label: 'KB live version', value: data.kbLiveVersion ?? '—' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className={cx('card card-interactive p-5', c.accent && 'primary-edge')}>
          <div
            className={cx(
              'text-3xl font-bold tracking-tight',
              'text-foreground',
              typeof c.value === 'string' && c.value.length > 8 && 'text-lg font-mono',
            )}
          >
            {c.value}
          </div>
          <div className="mt-1 text-xs text-muted-foreground uppercase tracking-wider">
            {c.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtUsd(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Governance() {
  const qc = useQueryClient();
  const canManage = useAppSelector((s) => s.auth.user?.permissions.includes('users.manage') ?? false);
  const { data, error } = useLoad<AdminSettings>('settings', () => adminApi.settings());

  const [spend, setSpend] = useState('');
  const [rate, setRate] = useState('');
  const [windowSec, setWindowSec] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  // Seed editable fields once the settings load (and after a successful save).
  useEffect(() => {
    if (data) {
      setSpend(String(data.spendLimitUsd));
      setRate(String(data.rateLimit));
      setWindowSec(String(data.rateWindowSeconds));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateSettings({
        spendLimitUsd: Number(spend),
        rateLimit: Number(rate),
        rateWindowSeconds: Number(windowSec),
      }),
    onSuccess: (next) => {
      qc.setQueryData(queryKeys.admin('settings'), next);
      setNotice({ tone: 'ok', text: 'Limits updated.' });
    },
    onError: (e) =>
      setNotice({ tone: 'err', text: e instanceof ApiError ? e.message : 'Save failed.' }),
  });

  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;

  const spentPct = data.spendLimitUsd > 0 ? Math.min(100, (data.spentUsd / data.spendLimitUsd) * 100) : 0;
  const dirty =
    Number(spend) !== data.spendLimitUsd ||
    Number(rate) !== data.rateLimit ||
    Number(windowSec) !== data.rateWindowSeconds;
  const valid = Number(spend) >= 0 && Number(rate) >= 1 && Number(windowSec) >= 1;

  const checkpoints: { icon: typeof KeyRound; title: string; body: string }[] = [
    {
      icon: KeyRound,
      title: 'Holds the provider key',
      body: `The ${data.provider} API key lives only on the server. No browser or client ever sees it — every model call is proxied through the gateway.`,
    },
    {
      icon: ScanLine,
      title: 'Checks for secrets & personal data',
      body: `Each prompt is scanned before it leaves the building. So far ${data.flaggedSecrets} secret and ${data.flaggedPii} personal-data matches were redacted.`,
    },
    {
      icon: ScrollText,
      title: 'Logs what happened',
      body: `Every call is recorded with actor, tokens, and cost. ${data.aiCalls.toLocaleString()} calls and ${data.aiTokens.toLocaleString()} tokens logged to date.`,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Gateway explainer */}
      <div className="card p-6">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold text-foreground">The AI gateway</h2>
          <Badge tone="primary">{data.provider}</Badge>
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Every AI call runs through a single server-side gateway. It holds the provider key, checks
          for secrets and personal data, enforces the limits below, and logs what happened.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {checkpoints.map((c) => (
            <div key={c.title} className="rounded-lg border border-border bg-muted/40 p-4">
              <div className="flex items-center gap-2 text-foreground">
                <c.icon className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">{c.title}</span>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{c.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <Badge tone="muted">standard · {data.modelStandard}</Badge>
          <Badge tone="muted">premium · {data.modelPremium}</Badge>
        </div>
      </div>

      {/* Spend vs cap */}
      <div className="card p-6">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-foreground">AI spend</h3>
          <span className="text-xs text-muted-foreground">
            {fmtUsd(data.spentUsd)} of {fmtUsd(data.spendLimitUsd)} cap
          </span>
        </div>
        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cx(
              'h-full rounded-full transition-all',
              spentPct >= 90 ? 'bg-destructive' : spentPct >= 70 ? 'bg-warning' : 'bg-primary',
            )}
            style={{ width: `${spentPct}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          When spend reaches the cap, the gateway stops further model calls until the limit is
          raised.
        </p>
      </div>

      {/* Editable limits */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-foreground">Limits</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {canManage
            ? 'Changes apply immediately to the gateway.'
            : 'Read-only — requires the users.manage permission to edit.'}
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <LimitField
            label="Spend limit (USD)"
            value={spend}
            onChange={setSpend}
            disabled={!canManage}
            min={0}
            step="1"
          />
          <LimitField
            label="Rate limit (calls)"
            value={rate}
            onChange={setRate}
            disabled={!canManage}
            min={1}
            step="1"
          />
          <LimitField
            label="Rate window (seconds)"
            value={windowSec}
            onChange={setWindowSec}
            disabled={!canManage}
            min={1}
            step="1"
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Each user may make up to {data.rateLimit} AI calls per {data.rateWindowSeconds}s window.
        </p>
        {canManage && (
          <div className="mt-4 flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={!dirty || !valid || save.isPending}
            >
              {save.isPending ? <Spinner /> : <Save className="h-4 w-4" />}
              Save limits
            </Button>
            {notice && (
              <span
                className={cx(
                  'text-xs font-medium',
                  notice.tone === 'ok' ? 'text-success' : 'text-destructive',
                )}
              >
                {notice.text}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LimitField({
  label,
  value,
  onChange,
  disabled,
  min,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  min: number;
  step: string;
}) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-[13px] font-semibold text-foreground">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cx(
          'w-full h-11 px-4 rounded-md bg-input border border-border text-foreground tabular-nums',
          'transition-colors focus:outline-none focus:border-primary focus-ring',
          'disabled:opacity-60 disabled:cursor-not-allowed',
        )}
      />
    </label>
  );
}

function Audit({ search }: { search: string }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const { data, error } = useLoad<PaginatedResponse<AuditRow>>(
    'audit',
    () => adminApi.audit({ search, page: currentPage, limit: itemsPerPage }),
    { search, page: currentPage, limit: itemsPerPage },
  );

  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;

  const totalItems = data.total;
  const totalPages = data.totalPages;
  const activePage = data.page;
  const startItem = totalItems === 0 ? 0 : (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col gap-4">
      {data.items.length === 0 ? (
        <State>{search ? 'No audit logs match your search.' : 'No audit logs found.'}</State>
      ) : (
        <Table head={['When', 'Actor', 'Action', 'Target']}>
          {data.items.map((r) => (
            <tr key={r.seq}>
              <td className={cx(td, 'text-muted-foreground whitespace-nowrap')}>
                {new Date(r.at).toLocaleString()}
              </td>
              <td className={td}>{r.actor_email ?? '—'}</td>
              <td className={td}>
                <code className="font-mono text-xs text-foreground">{r.action}</code>
              </td>
              <td className={cx(td, 'text-muted-foreground font-mono text-xs')}>
                {r.target_id ?? '—'}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-2 px-1 gap-4 flex-wrap border-t border-border/40 pt-4">
          <div className="text-xs text-muted-foreground select-none font-mono">
            Showing <span className="text-primary font-semibold">{startItem}</span> – <span className="text-primary font-semibold">{endItem}</span> of <span className="text-primary font-semibold">{totalItems}</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground select-none">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-input border border-border text-foreground text-[11px] rounded-md px-2 py-1 outline-none cursor-pointer focus:border-primary transition font-semibold"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              {activePage > 1 && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {activePage < totalPages && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kb() {
  const { data, error } = useLoad<KbVersionRow[]>('kb', () => adminApi.kb());
  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;
  return (
    <Table head={['Version', 'Status', 'Docs', 'Chunks', 'Created']}>
      {data.map((v) => (
        <tr key={v.label}>
          <td className={cx(td, 'font-mono text-xs')}>{v.label}</td>
          <td className={td}>
            {v.live ? <Badge tone="ok">live</Badge> : <Badge tone="muted">retired</Badge>}
          </td>
          <td className={td}>{v.docs}</td>
          <td className={td}>{v.chunks}</td>
          <td className={cx(td, 'text-muted-foreground whitespace-nowrap')}>
            {new Date(v.created_at).toLocaleDateString()}
          </td>
        </tr>
      ))}
    </Table>
  );
}

function Gaps({ search }: { search: string }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const { data, error } = useLoad<PaginatedResponse<GapRow>>(
    'gaps',
    () => adminApi.gaps({ search, page: currentPage, limit: itemsPerPage }),
    { search, page: currentPage, limit: itemsPerPage },
  );

  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;

  const totalItems = data.total;
  const totalPages = data.totalPages;
  const activePage = data.page;
  const startItem = totalItems === 0 ? 0 : (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col gap-4">
      {data.items.length === 0 ? (
        <State>{search ? 'No gaps match your search.' : 'No gaps yet — low-rated or deflected questions will appear here.'}</State>
      ) : (
        <Table head={['Question', 'Rating', 'Deflected', 'When', 'Trace']}>
          {data.items.map((g, i) => (
            <tr key={i}>
              <td className={cx(td, 'text-foreground')}>{g.question}</td>
              <td className={cx(td, 'text-warning whitespace-nowrap')}>{g.rating}★</td>
              <td className={td}>{g.deflected ? <Badge tone="danger">yes</Badge> : '—'}</td>
              <td className={cx(td, 'text-muted-foreground whitespace-nowrap')}>
                {new Date(g.created_at).toLocaleString()}
              </td>
              <td className={td}>
                {g.traceUrl ? (
                  <a
                    href={g.traceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    View trace <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-2 px-1 gap-4 flex-wrap border-t border-border/40 pt-4">
          <div className="text-xs text-muted-foreground select-none font-mono">
            Showing <span className="text-primary font-semibold">{startItem}</span> – <span className="text-primary font-semibold">{endItem}</span> of <span className="text-primary font-semibold">{totalItems}</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground select-none">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-input border border-border text-foreground text-[11px] rounded-md px-2 py-1 outline-none cursor-pointer focus:border-primary transition font-semibold"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              {activePage > 1 && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {activePage < totalPages && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Users({ search }: { search: string }) {
  const qc = useQueryClient();
  const me = useAppSelector((s) => s.auth.user);
  const canManage = me?.permissions.includes('users.manage') ?? false;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editing, setEditing] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<{ id: string; email: string } | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const { data, error } = useLoad<PaginatedResponse<AdminUserRow>>(
    'users',
    () => adminApi.users({ search, page: currentPage, limit: itemsPerPage }),
    { search, page: currentPage, limit: itemsPerPage },
  );

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });

  const deleteUser = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => {
      setDeletingUser(null);
      setDeleteErr(null);
      refresh();
    },
    onError: (e) => setDeleteErr(e instanceof ApiError ? e.message : 'Could not delete user.'),
  });

  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;

  const totalItems = data.total;
  const totalPages = data.totalPages;
  const activePage = data.page;
  const startItem = totalItems === 0 ? 0 : (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col gap-5">
      {canManage && <AddUser onCreated={refresh} />}

      {data.items.length === 0 ? (
        <State>{search ? 'No users match your search.' : 'No users found.'}</State>
      ) : (
        <Table head={canManage ? ['Username', 'Email', 'Status', 'Roles', ''] : ['Username', 'Email', 'Status', 'Roles']}>
          {data.items.map((u) =>
            editing === u.id ? (
              <UserEditRow
                key={u.id}
                user={u}
                isSelf={u.email === me?.email}
                onDone={() => {
                  setEditing(null);
                  refresh();
                }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <tr key={u.id}>
                <td className={cx(td, 'text-foreground font-medium')}>{u.display_name || u.email.split('@')[0]}</td>
                <td className={cx(td, 'text-muted-foreground')}>{u.email}</td>
                <td className={td}>
                  <Badge tone={u.status === 'active' ? 'ok' : u.status === 'disabled' ? 'danger' : 'muted'}>
                    {u.status}
                  </Badge>
                </td>
                <td className={td}>
                  <span className="flex flex-wrap gap-1.5">
                    {u.roles.length ? (
                      u.roles.map((r) => (
                        <Badge key={r} tone="muted">
                          {r}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </span>
                </td>
                {canManage && (
                  <td className={cx(td, 'text-right')}>
                    <div className="inline-flex gap-2">
                      <Button variant="subtle" size="sm" onClick={() => setEditing(u.id)}>
                        Edit
                      </Button>
                      {u.email !== me?.email && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setDeletingUser({ id: u.id, email: u.email })}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ),
          )}
        </Table>
      )}

      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-4 px-1 gap-4 flex-wrap border-t border-border/40 pt-4">
          <div className="text-xs text-muted-foreground select-none font-mono">
            Showing <span className="text-primary font-semibold">{startItem}</span> – <span className="text-primary font-semibold">{endItem}</span> of <span className="text-primary font-semibold">{totalItems}</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground select-none">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-input border border-border text-foreground text-[11px] rounded-md px-2 py-1 outline-none cursor-pointer focus:border-primary transition font-semibold"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              {activePage > 1 && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {activePage < totalPages && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {deletingUser && (
        <DeleteUserModal
          email={deletingUser.email}
          isPending={deleteUser.isPending}
          error={deleteErr}
          onConfirm={() => deleteUser.mutate(deletingUser.id)}
          onClose={() => {
            setDeletingUser(null);
            setDeleteErr(null);
          }}
        />
      )}
    </div>
  );
}

function AddUser({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['staff']);
  const [status, setStatus] = useState<string>('active');
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setRoles(['staff']);
    setStatus('active');
    setErr(null);
  };

  const create = useMutation({
    mutationFn: () => adminApi.createUser({ email: email.trim(), roles, status }),
    onSuccess: () => {
      reset();
      setOpen(false);
      onCreated();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not add user.'),
  });

  const toggleRole = (r: string) => setRoles([r]);

  const valid = /\S+@\S+\.\S+/.test(email.trim()) && roles.length > 0;

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-4 card p-4">
        <p className="text-sm text-muted-foreground">
          Pre-provision a teammate so they have the right access the moment they sign in with
          Microsoft.
        </p>
        <Button size="sm" onClick={() => setOpen(true)} className="shrink-0">
          <UserPlus className="h-4 w-4" />
          Add user
        </Button>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Add a user</h3>
        <button
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="block mb-1.5 text-[13px] font-semibold text-foreground">Work email</span>
          <input
            type="email"
            placeholder="jane.doe@stfox.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 px-4 rounded-md bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus-ring"
          />
        </label>
        <label className="block">
          <span className="block mb-1.5 text-[13px] font-semibold text-foreground">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full h-11 px-3 rounded-md bg-input border border-border text-foreground focus-ring"
          >
            {USER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-4">
        <span className="block mb-1.5 text-[13px] font-semibold text-foreground">Roles</span>
        <div className="flex flex-wrap gap-1.5">
          {ALL_ROLES.map((r) => {
            const on = roles.includes(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() => toggleRole(r)}
                className={cx(
                  'px-2.5 h-7 rounded-full text-[11px] font-semibold font-mono border transition-colors',
                  on
                    ? 'bg-primary text-primary-foreground border-transparent'
                    : 'bg-muted text-muted-foreground border-border hover:text-foreground',
                )}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
      {err && <p className="mt-3 text-xs text-destructive">{err}</p>}
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" onClick={() => create.mutate()} disabled={!valid || create.isPending}>
          {create.isPending ? <Spinner /> : <UserPlus className="h-4 w-4" />}
          Add user
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          disabled={create.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function UserEditRow({
  user,
  isSelf,
  onDone,
  onCancel,
}: {
  user: AdminUserRow;
  isSelf: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState(user.status);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => adminApi.updateUser(user.id, { status, roles }),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Save failed.'),
  });

  const toggleRole = (r: string) => setRoles([r]);

  const changed =
    status !== user.status ||
    roles.length !== user.roles.length ||
    roles.some((r) => !user.roles.includes(r));
  const valid = roles.length > 0;

  return (
    <tr className="bg-muted/40">
      <td className={cx(td, 'text-foreground font-medium')}>{user.display_name || user.email.split('@')[0]}</td>
      <td className={cx(td, 'text-muted-foreground')}>
        {user.email}
        {isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
      </td>
      <td className={td}>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md bg-input border border-border px-2 text-sm text-foreground focus-ring"
        >
          {USER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </td>
      <td className={td}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            {ALL_ROLES.map((r) => {
              const on = roles.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleRole(r)}
                  className={cx(
                    'px-2.5 h-7 rounded-full text-[11px] font-semibold font-mono border transition-colors',
                    on
                      ? 'bg-primary text-primary-foreground border-transparent'
                      : 'bg-muted text-muted-foreground border-border hover:text-foreground',
                  )}
                >
                  {r}
                </button>
              );
            })}
          </div>
          {!valid && <span className="text-xs text-destructive">Select at least one role.</span>}
          {err && <span className="text-xs text-destructive">{err}</span>}
        </div>
      </td>
      <td className={cx(td, 'text-right whitespace-nowrap')}>
        <div className="inline-flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={!changed || !valid || save.isPending}
          >
            {save.isPending ? <Spinner /> : 'Save'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function DeleteUserModal({
  email,
  onConfirm,
  onClose,
  error,
  isPending,
}: {
  email: string;
  onConfirm: () => void;
  onClose: () => void;
  error: string | null;
  isPending: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in-50 zoom-in-95 duration-150">
        <h3 className="text-lg font-semibold text-foreground mb-2">Delete User</h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Are you sure you want to permanently delete the user <span className="font-semibold text-foreground">"{email}"</span>? This will revoke all their access. This action cannot be undone.
        </p>
        {error && <p className="mb-4 text-xs text-destructive">{error}</p>}
        <div className="flex justify-end gap-3">
          <Button variant="subtle" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending ? <Spinner className="h-4 w-4" /> : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Usage({ search }: { search: string }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const { data, error } = useLoad<PaginatedUsageResponse>(
    'usage',
    () => adminApi.usage({ search, page: currentPage, limit: itemsPerPage }),
    { search, page: currentPage, limit: itemsPerPage },
  );

  if (error) return <State tone="error">{error}</State>;
  if (!data) return <State>Loading…</State>;

  const totalItems = data.total;
  const totalPages = data.totalPages;
  const activePage = data.page;
  const startItem = totalItems === 0 ? 0 : (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-col gap-5">
      {/* Global summary stats in place of the 'Add user' panel */}
      <div className="flex items-center justify-between gap-4 card p-5">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Total Token Usage</span>
          <span className="text-2xl font-bold text-foreground font-mono">
            {data.summary?.total_tokens?.toLocaleString() ?? 0}
          </span>
        </div>
        <div className="flex flex-col gap-1 text-right">
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Total Cost</span>
          <span className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 font-mono">
            {fmtUsd(data.summary?.total_cost ?? 0)}
          </span>
        </div>
      </div>

      {data.items.length === 0 ? (
        <State>{search ? 'No users match your search.' : 'No users found.'}</State>
      ) : (
        <Table head={['Username', 'Email', 'Status', 'Roles', 'Token usage', 'Token cost']}>
          {data.items.map((u) => (
            <tr key={u.id}>
              <td className={cx(td, 'text-foreground font-medium')}>{u.display_name || u.email.split('@')[0]}</td>
              <td className={cx(td, 'text-muted-foreground')}>{u.email}</td>
              <td className={td}>
                <Badge tone={u.status === 'active' ? 'ok' : u.status === 'disabled' ? 'danger' : 'muted'}>
                  {u.status}
                </Badge>
              </td>
              <td className={td}>
                <span className="flex flex-wrap gap-1.5">
                  {u.roles.length ? (
                    u.roles.map((r) => (
                      <Badge key={r} tone="muted">
                        {r}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>
              </td>
              <td className={cx(td, 'font-mono text-xs')}>{u.token_usage.toLocaleString()}</td>
              <td className={cx(td, 'font-mono text-xs text-cyan-600 dark:text-cyan-400 font-semibold')}>{fmtUsd(u.total_cost)}</td>
            </tr>
          ))}
        </Table>
      )}

      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-4 px-1 gap-4 flex-wrap border-t border-border/40 pt-4">
          <div className="text-xs text-muted-foreground select-none font-mono">
            Showing <span className="text-primary font-semibold">{startItem}</span> – <span className="text-primary font-semibold">{endItem}</span> of <span className="text-primary font-semibold">{totalItems}</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground select-none">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-input border border-border text-foreground text-[11px] rounded-md px-2 py-1 outline-none cursor-pointer focus:border-primary transition font-semibold"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-1">
              {activePage > 1 && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous Page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              {activePage < totalPages && (
                <Button
                  variant="subtle"
                  size="sm"
                  className="w-8 h-8 !p-0"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next Page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
